import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AddressLabel } from "@/components/address-label";
import { Input } from "@/components/ui/input";
import { CustomSelect } from "@/components/ui/custom-select";
import { statesAndLgas } from "@/lib/states-and-lgas";
import { useTestPaymentStore } from "../hooks/use-test-payment-store";
import { TestAddress } from "../types";
import { AlertCircle } from "lucide-react";

export function TestAddressSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { testAddress, setTestAddress } = useTestPaymentStore();

  const formatAddress = (address: TestAddress) => {
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.lga) parts.push(address.lga);
    if (address.state) parts.push(address.state + " state");
    if (address.landmark) parts.push(`(Near ${address.landmark})`);
    return parts.join(", ");
  };

  return (
    <div className="mt-4">
      <h3 className="font-medium text-gray-900 mb-2">Your address</h3>
      <p className="text-sm text-gray-500 mb-4">
        Please provide your address. You will be contacted with the next steps.
      </p>

      {testAddress ? (
        <div className="bg-gray-50 p-3 rounded-md">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
            <p className="text-gray-700 flex-1">{formatAddress(testAddress)}</p>
            <Button
              variant="ghost"
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1 h-auto text-sm w-full sm:w-auto"
              onClick={() => setIsModalOpen(true)}
            >
              Change address
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => setIsModalOpen(true)}
        >
          Add address
        </Button>
      )}

      <TestAddressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(address) => {
          setTestAddress(address);
          setIsModalOpen(false);
        }}
      />

    </div>
  );
}

interface TestAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: TestAddress) => void;
}

function TestAddressModal({
  isOpen,
  onClose,
  onSave,
}: TestAddressModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<TestAddress>({
    street: "",
    city: "",
    landmark: "",
    state: "",
    lga: "",
  });


  const handleUpdateAddress = (name: string, value: string) => {
    setError(null);

    if (!name || name === "") return;
    if (name === "state") {
      setAddress((prev) => ({
        ...prev,
        lga: "",
        [name]: value,
      }));
    } else {
      setAddress((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = () => {
    // Check if required fields are filled
    const missingFields = [];
    if (!address.state) missingFields.push("State");
    if (!address.lga) missingFields.push("LGA");
    if (!address.city) missingFields.push("City");
    if (!address.street) missingFields.push("Street");

    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    onSave(address);
    onClose();
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Enter your delivery address</DialogTitle>
          <DialogDescription>
            This address will be used to deliver your drugs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 px-1 overflow-y-auto flex-1 min-h-0">

          <div>
            <AddressLabel htmlFor="street" className="mb-1 block" required>Street</AddressLabel>
            <Input
              id="street"
              value={address.street}
              onChange={(e) => handleUpdateAddress("street", e.target.value)}
              placeholder="Enter your street address"
            />
          </div>

          <div>
            <AddressLabel htmlFor="city" className="mb-1 block" required>City</AddressLabel>
            <Input
              id="city"
              value={address.city}
              onChange={(e) => handleUpdateAddress("city", e.target.value)}
              placeholder="Enter your city"
            />
          </div>

          <div>
            <AddressLabel htmlFor="state" className="mb-1 block" required>State</AddressLabel>
            <CustomSelect
              id="state"
              value={address.state}
              onChange={(e) => handleUpdateAddress("state", e.target.value)}
              options={Object.keys(statesAndLgas).map((state) => ({
                value: state,
                label: state,
              }))}
              placeholder="Select a state"
            />
          </div>

          <div>
            <AddressLabel htmlFor="lga" className="mb-1 block" required>LGA</AddressLabel>
            <CustomSelect
              id="lga"
              value={address.lga}
              onChange={(e) => handleUpdateAddress("lga", e.target.value)}
              disabled={!address.state}
              options={(statesAndLgas[address.state] || []).map((lga) => ({
                value: lga,
                label: lga,
              }))}
              placeholder={address.state ? "Select LGA" : "Select a state first"}
            />
          </div>

          <div>
            <AddressLabel htmlFor="landmark" className="mb-1 block">Closest Landmark</AddressLabel>
            <Input
              id="landmark"
              value={address.landmark || ""}
              onChange={(e) => handleUpdateAddress("landmark", e.target.value)}
              placeholder="Enter the closest landmark (optional)"
            />
          </div>

        </div>

        {/* Fixed footer with buttons */}
        <div className="flex-shrink-0 pt-4 border-t border-gray-200">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">
                  {error}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Address
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}